import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Box, TextField, Button, Typography, Paper, Container, Grid,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  FormControl, InputLabel, Select, MenuItem, Chip, Divider, IconButton, Tooltip, InputAdornment, Stack,
  FormControlLabel, Switch, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import axios from '../../api/axiosConfig';
import CategoryIcon from '@mui/icons-material/Category';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import SortIcon from '@mui/icons-material/Sort';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AssignmentIcon from '@mui/icons-material/Assignment';

const CategoryManage = () => {
  const API_URL = '/api/category';
  const CODE_URL = '/api/common-code/group/00003'; 
  const THEME_NAVY = '#1a233a';
  
  const [categories, setCategories] = useState([]);
  const [allQuestions, setAllQuestions] = useState([]);
  const [difficultyCodes, setDifficultyCodes] = useState([]); 
  
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const [isEditMode, setIsEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  
  // 배점 관리 팝업 관련 상태
  const [openScoreDialog, setOpenScoreDialog] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [tempScores, setTempScores] = useState({});
  
  const [searchTerm, setSearchTerm] = useState('');
  const [searchUseYn, setSearchUseYn] = useState('ALL');

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    difficultyLevel: '', 
    useYn: 'Y',
    sortApplYn: 'N' 
  });

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    setHasSearched(true);
    try {
      const response = await axios.get(API_URL);
      setCategories(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("카테고리 로딩 실패:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [qRes, codeRes] = await Promise.all([
          axios.get('/api/question'),
          axios.get(CODE_URL) 
        ]);
        setAllQuestions(qRes.data || []);
        setDifficultyCodes(codeRes.data || []); 
        
        if (codeRes.data?.length > 0) {
          setFormData(prev => ({ ...prev, difficultyLevel: codeRes.data[0].detailCode }));
        }
      } catch (error) {
        console.error("기초 데이터 로딩 실패:", error);
      }
    };
    fetchInitialData();
  }, []);

  const filteredCategories = useMemo(() => {
    return categories.filter((item) => {
      const matchesText = item.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesUseYn = searchUseYn === 'ALL' || item.useYn === searchUseYn;
      return matchesText && matchesUseYn;
    });
  }, [categories, searchTerm, searchUseYn]);

  const targetQuestions = useMemo(() => {
    if (!selectedCategory) return [];
    return allQuestions.filter(q => q.categoryPk === selectedCategory.categoryPk);
  }, [allQuestions, selectedCategory]);

  const currentTotalScore = useMemo(() => {
    return targetQuestions.reduce((sum, q) => {
      const score = tempScores[q.questionPk] !== undefined ? tempScores[q.questionPk] : (q.score || 0);
      return sum + Number(score);
    }, 0);
  }, [targetQuestions, tempScores]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSwitchChange = (e) => {
    setFormData({ ...formData, sortApplYn: e.target.checked ? 'Y' : 'N' });
  };

  const handleScoreChange = (questionPk, value) => {
    setTempScores(prev => ({ ...prev, [questionPk]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditMode) {
        await axios.put(`${API_URL}/${editId}`, formData);
        alert('카테고리가 수정되었습니다.');
      } else {
        await axios.post(API_URL, formData);
        alert('카테고리가 등록되었습니다.');
      }
      resetForm();
      fetchCategories();
    } catch (error) {
      alert('처리 실패: ' + (error.response?.data || '알 수 없는 오류'));
    }
  };

  const handleSaveScores = async () => {
    if (currentTotalScore !== 100) {
      if (!window.confirm(`현재 합계가 ${currentTotalScore}점입니다. 100점이 아니어도 저장하시겠습니까?`)) return;
    }
    try {
      const updatePromises = Object.entries(tempScores).map(([pk, score]) => 
        axios.put(`/api/question/${pk}/score`, { score: Number(score) })
      );
      await Promise.all(updatePromises);
      alert('배점이 성공적으로 저장되었습니다.');
      setTempScores({});
      setOpenScoreDialog(false);
      fetchCategories(); 
    } catch (error) {
      console.error(error);
      alert('배점 저장 실패: ' + (error.response?.data?.message || '서버 오류가 발생했습니다.'));
    }
  };

  const handleDelete = async (pk) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    try {
      await axios.delete(`${API_URL}/${pk}`);
      alert('삭제되었습니다.');
      fetchCategories();
    } catch (error) {
      alert('삭제 실패: ' + (error.response?.data?.message || '사용 중인 카테고리는 삭제할 수 없습니다.'));
    }
  };

  const handleEdit = (category) => {
    setIsEditMode(true);
    setEditId(category.categoryPk);
    setFormData({
      name: category.name,
      description: category.description || '',
      difficultyLevel: category.difficultyLevel,
      useYn: category.useYn,
      sortApplYn: category.sortApplYn || 'N'
    });
  };

  const handleOpenScoreDialog = (category) => {
    setSelectedCategory(category);
    setTempScores({}); // 팝업 열 때 임시 점수 초기화
    setOpenScoreDialog(true);
  };

  const resetForm = () => {
    setIsEditMode(false);
    setEditId(null);
    setFormData({ 
      name: '', 
      description: '', 
      difficultyLevel: difficultyCodes[0]?.detailCode || '', 
      useYn: 'Y',
      sortApplYn: 'N'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <CategoryIcon sx={{ mr: 1, color: THEME_NAVY, fontSize: 30 }} />
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>카테고리 마스터 & 배점 관리</Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', lg: 'row' }, alignItems: 'flex-start' }}>
        
        {/* --- 등록/수정 폼 영역 --- */}
        <Paper elevation={3} sx={{ p: 3, width: { xs: '100%', lg: '400px' }, flexShrink: 0, borderTop: isEditMode ? '4px solid #ed6c02' : `4px solid ${THEME_NAVY}`, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', fontWeight: 'bold' }}>
              {isEditMode ? <EditIcon sx={{ mr: 1 }} color="warning" /> : <AddCircleOutlineIcon sx={{ mr: 1 }} color="primary" />}
              {isEditMode ? '카테고리 수정' : '카테고리 등록'}
            </Typography>
            {isEditMode && <IconButton size="small" onClick={resetForm}><CloseIcon /></IconButton>}
          </Box>
          <Divider sx={{ mb: 2 }} />
          <form onSubmit={handleSubmit}>
            <TextField fullWidth label="카테고리 명" name="name" value={formData.name} onChange={handleChange} margin="normal" required size="small" />
            <TextField fullWidth label="설명" name="description" value={formData.description} onChange={handleChange} margin="normal" multiline rows={2} size="small" />
            
            <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
              <FormControl fullWidth margin="normal" size="small">
                <InputLabel id="form-diff-label">난이도</InputLabel>
                <Select labelId="form-diff-label" name="difficultyLevel" value={formData.difficultyLevel} label="난이도" onChange={handleChange}>
                  {difficultyCodes.map(code => (
                    <MenuItem key={code.detailCode} value={code.detailCode}>{code.detailCodeName}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth margin="normal" size="small">
                <InputLabel id="form-use-label">사용여부</InputLabel>
                <Select labelId="form-use-label" name="useYn" value={formData.useYn} label="사용여부" onChange={handleChange}>
                  <MenuItem value="Y">Y (사용중)</MenuItem>
                  <MenuItem value="N">N (미사용)</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ 
              mt: 2, p: 1.5, borderRadius: 2, bgcolor: '#f8f9fa', border: '1px solid #e0e0e0',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center' 
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {formData.sortApplYn === 'Y' ? <SortIcon color="primary" fontSize="small"/> : <ShuffleIcon color="action" fontSize="small"/>}
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: THEME_NAVY }}>
                  문제 순서: {formData.sortApplYn === 'Y' ? '정해진 순서' : '무작위'}
                </Typography>
              </Box>
              <FormControlLabel 
                control={<Switch checked={formData.sortApplYn === 'Y'} onChange={handleSwitchChange} color="primary" size="small"/>} 
                label={<Typography variant="caption">순서고정</Typography>}
              />
            </Box>
            
            <Button fullWidth variant="contained" type="submit" color={isEditMode ? "warning" : "primary"} sx={{ mt: 3, py: 1.2, backgroundColor: isEditMode ? '' : THEME_NAVY, fontWeight: 'bold', fontSize: '1rem' }}>
              {isEditMode ? '수정 내용 저장' : '새 카테고리 저장'}
            </Button>
          </form>
        </Paper>

        {/* --- 목록 영역 --- */}
        <Box sx={{ flexGrow: 1, width: '100%' }}>
          <Paper elevation={3} sx={{ p: 2 }}>
            <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', backgroundColor: '#fdfdfd', p: 1, borderRadius: 1 }}>
              <TextField 
                size="small" 
                placeholder="카테고리명 검색" 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && fetchCategories()}
                sx={{ flexGrow: 1, minWidth: '200px' }}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
              />
              <FormControl size="small" sx={{ minWidth: '130px' }}>
                <InputLabel>상태</InputLabel>
                <Select label="상태" value={searchUseYn} onChange={(e) => setSearchUseYn(e.target.value)}>
                  <MenuItem value="ALL">전체상태</MenuItem>
                  <MenuItem value="Y">사용중</MenuItem>
                  <MenuItem value="N">미사용</MenuItem>
                </Select>
              </FormControl>
              
              <Button variant="contained" startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />} onClick={fetchCategories} sx={{ bgcolor: THEME_NAVY }}>조회</Button>
              <Button startIcon={<RefreshIcon />} size="small" onClick={() => { setSearchTerm(''); setSearchUseYn('ALL'); setCategories([]); setHasSearched(false); }}>초기화</Button>
            </Box>

            <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8f9fa' }}>명칭 (문제수)</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#f8f9fa' }}>난이도</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#f8f9fa' }}>문제 순서</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#f8f9fa', minWidth: '90px' }}>상태</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8f9fa' }}>등록 정보</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8f9fa' }}>수정 정보</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#f8f9fa' }}>관리</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={7} align="center" sx={{ py: 10 }}><CircularProgress /></TableCell></TableRow>
                  ) : !hasSearched ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 15 }}>
                        <InfoOutlinedIcon sx={{ fontSize: 40, color: 'grey.300', mb: 1 }} />
                        <Typography color="text.secondary" fontWeight="bold">조회 버튼을 클릭해주세요.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : filteredCategories.length === 0 ? (
                    <TableRow><TableCell colSpan={7} align="center" sx={{ py: 10 }}>검색 결과가 없습니다.</TableCell></TableRow>
                  ) : (
                    filteredCategories.map((row) => (
                      <TableRow key={row.categoryPk} hover sx={{ backgroundColor: editId === row.categoryPk ? '#fff4e5' : 'inherit' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>{row.name} ({allQuestions.filter(q => q.categoryPk === row.categoryPk).length})</TableCell>
                        <TableCell align="center">
                          <Chip label={difficultyCodes.find(c => c.detailCode === String(row.difficultyLevel))?.detailCodeName || row.difficultyLevel} size="small" variant="outlined" sx={{ fontWeight: 'bold', color: THEME_NAVY }} />
                        </TableCell>
                        <TableCell align="center">
                          {row.sortApplYn === 'Y' ? <Tooltip title="순서대로"><SortIcon fontSize="small" color="primary" /></Tooltip> : <Tooltip title="무작위"><ShuffleIcon fontSize="small" color="disabled" /></Tooltip>}
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={row.useYn === 'Y' ? '사용중' : '미사용'} size="small" color={row.useYn === 'Y' ? 'success' : 'default'} sx={{ fontWeight: 'bold', minWidth: '70px' }} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" display="block" sx={{ fontWeight: 'bold' }}>{row.createUser || 'SYSTEM'}</Typography>
                          <Typography variant="caption" color="text.secondary">{formatDateTime(row.createDate)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" display="block" sx={{ fontWeight: 'bold' }}>{row.updateUser || '-'}</Typography>
                          <Typography variant="caption" color="text.secondary">{formatDateTime(row.updateDate)}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={0.5} justifyContent="center">
                            <Tooltip title="배점 설정"><IconButton size="small" color="info" onClick={() => handleOpenScoreDialog(row)}><AssignmentIcon fontSize="small" /></IconButton></Tooltip>
                            <Tooltip title="수정"><IconButton size="small" color="primary" onClick={() => handleEdit(row)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                            <Tooltip title="삭제"><IconButton size="small" color="error" onClick={() => handleDelete(row.categoryPk)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      </Box>

      {/* --- 배점 관리 팝업 (Dialog) --- */}
      <Dialog open={openScoreDialog} onClose={() => setOpenScoreDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: THEME_NAVY, color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssignmentIcon />
            <Typography variant="h6">배점 설정 - {selectedCategory?.name}</Typography>
          </Box>
          <IconButton size="small" onClick={() => setOpenScoreDialog(false)} sx={{ color: 'white' }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 3, p: 2, borderRadius: 2, bgcolor: currentTotalScore === 100 ? '#e8f5e9' : '#fff3e0', textAlign: 'center', border: '1px solid', borderColor: currentTotalScore === 100 ? '#2e7d32' : '#ed6c02' }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: currentTotalScore === 100 ? '#2e7d32' : '#ed6c02' }}>
              합계: {currentTotalScore} / 100
            </Typography>
            <Typography variant="caption" color="text.secondary">모든 문제의 배점 합계가 100점이 되도록 설정 권장</Typography>
          </Box>
          <TableContainer sx={{ maxHeight: '400px' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8f9fa' }}>문제 제목</TableCell>
                  <TableCell align="center" width="100" sx={{ fontWeight: 'bold', bgcolor: '#f8f9fa' }}>배점(점)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {targetQuestions.length === 0 ? (
                  <TableRow><TableCell colSpan={2} align="center" sx={{ py: 3 }}>등록된 문제가 없습니다.</TableCell></TableRow>
                ) : (
                  targetQuestions.map((q) => (
                    <TableRow key={q.questionPk} hover>
                      <TableCell sx={{ fontSize: '0.9rem' }}>{q.title}</TableCell>
                      <TableCell align="center">
                        <TextField 
                          size="small" 
                          type="number" 
                          variant="outlined"
                          value={tempScores[q.questionPk] !== undefined ? tempScores[q.questionPk] : (q.score || 0)} 
                          onChange={(e) => handleScoreChange(q.questionPk, e.target.value)} 
                          inputProps={{ style: { textAlign: 'center', fontWeight: 'bold' } }} 
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#f8f9fa' }}>
          <Button onClick={() => setOpenScoreDialog(false)} color="inherit" variant="outlined">취소</Button>
          <Button 
            variant="contained" 
            startIcon={<SaveIcon />} 
            onClick={handleSaveScores} 
            disabled={Object.keys(tempScores).length === 0}
            sx={{ bgcolor: THEME_NAVY, px: 4 }}
          >
            배점 정보 저장
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CategoryManage;